import { useMemo, useState } from "react"
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfWeek,
  format,
  isSameDay,
  startOfDay,
  startOfWeek,
  subDays,
} from "date-fns"
import { CircularProgress } from "@mui/material"

import { createAccountStore } from "@/stores/account"
import { AccountTypeKey } from "@/constants/account"
import { useFilteredActivitiesQuery } from "@/hooks/api/useActivityQuery"
import { useWeatherQuery } from "@/hooks/api/useWeatherQuery"
import { useGeolocation } from "@/hooks/useGeolocation"
import { getActivityColor } from "@/app/activity/list/utils"
import { useCalendarNav } from "@/app/home/hooks/useCalendarNav"
import { weatherIconFromCode } from "@/app/home/utils/weatherIcon"
import ActivityInfoDialog from "@/app/activity/list/activityInfoDialog/ActivityInfoDialog"
import type { FilteredActivityItem } from "@/app/api/activity"
import type { Activity } from "@/types/activity"

export default function ActivityCalendarWeeklyWidget() {
  const isPlayerAccount = createAccountStore((state) => state.info?.type === AccountTypeKey.Player)
  const { currentDate } = useCalendarNav()

  const [detailState, setDetailState] = useState<{
    open: boolean
    anchorEl: HTMLElement | null
    activity: Activity | null
  }>({ open: false, anchorEl: null, activity: null })
  const openDetail = (activity: Activity, anchorEl: HTMLElement) => {
    if (isPlayerAccount) return
    setDetailState({ open: true, anchorEl, activity })
  }
  const closeDetail = () => setDetailState((s) => ({ ...s, open: false }))

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 0 }), [currentDate])
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 0 }), [currentDate])
  const weekDates = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: weekEnd }),
    [weekStart, weekEnd],
  )

  const { data: activityList, isLoading } = useFilteredActivitiesQuery({
    start: subDays(weekStart, 1).valueOf(),
    end: addDays(weekEnd, 1).valueOf(),
  })

  // 현재 위치 → 주간 날씨 (보여지는 주에 맞춰 pastDays/forecastDays 동적 계산)
  const { coords } = useGeolocation()
  const { pastDays, forecastDays } = useMemo(() => {
    const today = startOfDay(new Date())
    const past = Math.max(0, differenceInCalendarDays(today, weekStart))
    const forecast = Math.max(1, differenceInCalendarDays(weekEnd, today) + 1)
    return { pastDays: past, forecastDays: forecast }
  }, [weekStart, weekEnd])

  const { data: weather } = useWeatherQuery({
    latitude: coords?.latitude ?? 0,
    longitude: coords?.longitude ?? 0,
    pastDays,
    forecastDays,
    enabled: !!coords,
  })

  const weatherByDate = useMemo(() => {
    const map = new Map<
      string,
      {
        tempMax: number | null
        tempMin: number | null
        humidity: number | null
        weatherCode: number | null
      }
    >()
    ;(weather ?? []).forEach((w) => {
      map.set(w.date, {
        tempMax: w.tempMax,
        tempMin: w.tempMin,
        humidity: w.humidity,
        weatherCode: w.weatherCode,
      })
    })
    return map
  }, [weather])

  const activitiesByDate = useMemo(() => {
    const map = new Map<string, FilteredActivityItem[]>()
    ;(activityList ?? []).forEach((a) => {
      if (!a.dateStart) return
      const d = new Date(a.dateStart)
      if (Number.isNaN(d.getTime())) return
      const key = format(d, "yyyy-MM-dd")
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(a)
    })
    return map
  }, [activityList])

  const today = new Date()

  return (
    <div className="ActivityCalendarWeeklyWidget">
      {isLoading && (
        <div className="ActivityCalendarWeeklyWidget__loading">
          <CircularProgress size={24} />
        </div>
      )}

      <div className="ActivityCalendarWeeklyWidget__grid">
        {weekDates.map((date) => {
          const key = format(date, "yyyy-MM-dd")
          const isToday = isSameDay(date, today)
          const dayWeather = weatherByDate.get(key)
          const dayActivities = activitiesByDate.get(key) ?? []

          return (
            <div key={key} className="ActivityCalendarWeeklyWidget__column">
              <div className="ActivityCalendarWeeklyWidget__col-header">
                <div className="ActivityCalendarWeeklyWidget__col-header-row">
                  <span className="ActivityCalendarWeeklyWidget__weekday">
                    {format(date, "EEE").toUpperCase()}
                  </span>
                  <span
                    className={`ActivityCalendarWeeklyWidget__day ${isToday ? "ActivityCalendarWeeklyWidget__day--today" : ""}`}>
                    {format(date, "d")}
                  </span>
                </div>
                <div className="ActivityCalendarWeeklyWidget__weather">
                  {dayWeather ? (
                    <>
                      <span className="ActivityCalendarWeeklyWidget__weather-icon">
                        {weatherIconFromCode(dayWeather.weatherCode, 14)}
                      </span>
                      <span>
                        {formatTemp(dayWeather.tempMin)} / {formatTemp(dayWeather.tempMax)}
                      </span>
                      <span className="ActivityCalendarWeeklyWidget__humidity">
                        {formatHumidity(dayWeather.humidity)}
                      </span>
                    </>
                  ) : (
                    <span className="ActivityCalendarWeeklyWidget__weather-placeholder">
                      &nbsp;
                    </span>
                  )}
                </div>
              </div>

              <div className="ActivityCalendarWeeklyWidget__col-body">
                {dayActivities.map((activity) => (
                  <div
                    key={activity.idfActivity}
                    className={`ActivityCalendarWeeklyWidget__event ${!isPlayerAccount ? "ActivityCalendarWeeklyWidget__event--clickable" : ""}`}
                    style={{ background: getActivityColor(activity.type) }}
                    onClick={(e) => {
                      if (isPlayerAccount || !activity.idfActivity) return
                      openDetail(activity as unknown as Activity, e.currentTarget)
                    }}>
                    {activity.name}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
      {detailState.activity && (
        <ActivityInfoDialog
          open={detailState.open}
          anchorEl={detailState.anchorEl}
          activity={detailState.activity}
          onClose={closeDetail}
          onEdit={() => {}}
        />
      )}
    </div>
  )
}

function formatTemp(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "--"
  return `${Math.round(value)}°`
}

function formatHumidity(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "--%"
  return `${Math.round(value)}%`
}
